import { GoAlert } from "react-icons/go";
import { FaArrowRight } from "react-icons/fa";
import { LuClock } from "react-icons/lu";
import { useParams, useRouteContext, Link } from "@tanstack/react-router";
import { useAuthenticatedSuspenseQuery } from "@/queries/shared";
import { useCSPPIDMatches } from "@/queries";
import { MatchLEAStatus } from "@/models/mid";
import { type AnomalyRun } from "@/models/file-submission";
import { type UseSuspenseQueryResult } from "@tanstack/react-query";

const midBadgeStyle = (midCount: number): React.CSSProperties => ({
  backgroundColor: midCount > 0 ? "#DC2626" : "#f1f5f9",
  color: midCount > 0 ? "#fff" : "#1e293b",
  fontWeight: 700,
  fontSize: "0.786rem",
  minWidth: 36,
  height: 24,
  padding: "0 8px",
  borderRadius: 4,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const MidCountBadge = ({ leaid }: { leaid: string }) => {
  const { data: matchesData } = useCSPPIDMatches(leaid);
  const midCount = (matchesData ?? []).filter(
    (m) => m.Status === MatchLEAStatus.UNRESOLVED_ACTIVE_LEA
  ).length;
  return <span style={midBadgeStyle(midCount)}>{midCount}</span>;
};

const DashboardDataDiscrepancies = () => {
  const { leaid } = useParams({ from: "/$leaid" });
  const leaContext = useRouteContext({ from: "/$leaid" });
  const canViewMids = !!leaContext.hasRole("CHLDView");

  const { data: anomalyRun }: UseSuspenseQueryResult<AnomalyRun, Error> =
    useAuthenticatedSuspenseQuery(
      `/json/anomaly_run/${leaid}/0`,
      "filesubmissions"
    );

  const anomalies = anomalyRun?.Anomalies ?? [];

  // Same filters as DiscrepanciesTab and SSIDSyncTab
  const ssidCount = anomalies.filter((a: any) =>
    a.ErrorCode?.startsWith("SSID")
  ).length;
  const errorCount = anomalies.filter(
    (a: any) => !a.ErrorCode?.startsWith("SSID")
  ).length;

  const lastCheck = anomalyRun?.EndTime
    ? new Date(anomalyRun.EndTime).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "N/A";

  return (
    <div
      className="card border-0 shadow-sm h-100"
      style={{ borderRadius: "0.5rem" }}
    >
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{
          backgroundColor: "#334155",
          borderRadius: "0.5rem 0.5rem 0 0",
          color: "#fff",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <GoAlert size={16} color="#fff" />
          <h2
            style={{
              margin: 0,
              fontWeight: 600,
              fontSize: "0.85rem",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Discrepancies
          </h2>
        </div>
        <Link
          to="/$leaid/discrepancies"
          params={{ leaid }}
          className="d-flex align-items-center gap-1"
          style={{ color: "#fff", fontSize: "0.85rem", textDecoration: "none" }}
        >
          <span aria-hidden="true">View All</span>
          <span className="visually-hidden">View All Discrepancies</span>
          <FaArrowRight size={12} aria-hidden="true" />
        </Link>
      </div>

      <div className="px-3 py-4">
        <div
          className="d-flex align-items-center gap-2 mb-4 text-muted"
          style={{ fontSize: "0.8rem" }}
        >
          <LuClock size={14} />
          <span>
            Last check:{" "}
            <strong style={{ color: "#0f172a" }}>{lastCheck}</strong>
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* Errors → #discrepancies tab */}
          <Link
            to="/$leaid/discrepancies"
            params={{ leaid }}
            hash="discrepancies"
            className="d-flex align-items-center justify-content-between flex-grow-1 text-decoration-none"
            style={{
              padding: "12px 16px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              transition: "border-color 0.15s ease, background 0.15s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "#94a3b8")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "#e2e8f0")
            }
          >
            <span
              style={{
                fontSize: "0.786rem",
                fontWeight: 600,
                color: "#0f172a",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Data Discrepancies
            </span>
            <span
              style={{
                backgroundColor: errorCount > 0 ? "#DC2626" : "#f1f5f9",
                color: errorCount > 0 ? "#fff" : "#1e293b",
                fontWeight: 700,
                fontSize: "0.786rem",
                minWidth: 36,
                height: 24,
                padding: "0 8px",
                borderRadius: 4,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {errorCount}
            </span>
          </Link>

          {/* SSID → #ssid-sync tab */}
          <Link
            to="/$leaid/discrepancies"
            params={{ leaid }}
            hash="ssid-sync"
            className="d-flex align-items-center justify-content-between flex-grow-1 text-decoration-none"
            style={{
              padding: "12px 16px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              transition: "border-color 0.15s ease, background 0.15s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "#94a3b8")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "#e2e8f0")
            }
          >
            <span
              style={{
                fontSize: "0.786rem",
                fontWeight: 600,
                color: "#0f172a",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              SSID Sync
            </span>
            <span
              style={{
                backgroundColor: ssidCount > 0 ? "#B45309" : "#f1f5f9",
                color: ssidCount > 0 ? "#fff" : "#1e293b",
                fontWeight: 700,
                fontSize: "0.786rem",
                minWidth: 36,
                height: 24,
                padding: "0 8px",
                borderRadius: 4,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {ssidCount}
            </span>
          </Link>

          {/* MIDs → #mids tab */}
          <Link
            to="/$leaid/discrepancies"
            params={{ leaid }}
            hash="mids"
            className="d-flex align-items-center justify-content-between flex-grow-1 text-decoration-none"
            style={{
              padding: "12px 16px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              transition: "border-color 0.15s ease, background 0.15s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "#94a3b8")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "#e2e8f0")
            }
          >
            <span
              style={{
                fontSize: "0.786rem",
                fontWeight: 600,
                color: "#0f172a",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              MIDs
            </span>
            {canViewMids ? (
              <MidCountBadge leaid={leaid} />
            ) : (
              <span style={midBadgeStyle(0)}>0</span>
            )}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardDataDiscrepancies;
